import type Store from "@ember-data/store";
import Route from "@ember/routing/route";
import { service } from "@ember/service";
import type BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type Patient from "parog-web/models/patient";
import type SemeiaSessionService from "parog-web/services/session";

export default class BioResultsEditionRoute extends Route {
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  override queryParams = {
    sourceJeId: {
      refreshModel: true
    }
  };

  override deactivate(): void {
    const model = this.modelFor(
      this.routeName
    ) as BioExamResultsReceivedJournalEvent;
    // Unload new bioExamResult as they are not save directly and EmberData doesn't merge theses objects with object returned by the back
    model.bioExamResults.slice().forEach(bioExamResult => {
      if (!bioExamResult.id) bioExamResult.unloadRecord();
    });
    if (
      model.isNew &&
      // 'deleteRecord' can fail if the store is currently destroying during a test
      !model.store.isDestroying
    ) {
      model.deleteRecord();
    }
  }

  override async model(params): Promise<BioExamResultsReceivedJournalEvent> {
    const patient = this.modelFor(
      this.session.patient
        ? "patient-web.protected"
        : "medics-web.protected.patients.patient"
    ) as Patient;

    await this.fetchPreferredUnitOfHealthCentre(patient.healthCentre);

    const sourceJeId = params["sourceJeId"];

    if (!sourceJeId) {
      return this.store.createRecord(
        "bio-exam-results-received-journal-event",
        {
          patient: patient,
          bioExamResults: [],
          occurredAt: this.session.medic ? new Date() : undefined
        }
      );
    }

    const model = await this.store.findRecord(
      "bio-exam-results-received-journal-event",
      sourceJeId,
      {
        include: [
          "creator",
          "file-attachments-without-zip",
          "apicrypt-message-received-journal-events",
          "bio-exam-results"
        ].join(","),
        reload: true
      }
    );

    return model;
  }

  private async fetchPreferredUnitOfHealthCentre(healthCentre) {
    await this.store.findRecord("health-centre", healthCentre.id, {
      include: "health-centre-loinc-preferred-units",
      reload: true
    });
  }
}
